/** Provides the Browser toolbar, including ordered logout and API-service cleanup. */
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  lazy, Suspense, useEffect, useRef, useState,
} from 'react';
import {type ILayoutApi} from 'flexlayout-react';
import cn from 'classnames';
import {ComponentErrorBoundary} from '@qep/shared/src/components/ComponentErrorBoundary/ComponentErrorBoundary';
import {getQepSystem} from '@qep/shared/src';
import {getAppEnv} from '@qep/shared/src/utils/runtime-config';
import {requestServiceWorkerVersion} from '../../utils/request-service-worker-version.js';
import {cleanupSubscriptionsBeforeLogout} from '../../utils/logout.js';
import {PreferencesModal} from '../preferences/preferences-modal.js';
import darkLogo from '../../../assets/darkLogo.png';
import {NetworkStatusMenu} from './NetworkStatus/network-status-menu.js';
import NotificationCenter from './NotificationCenter/notification-center.js';
import './toolbar.scss';
import {SystemNotificationBanner} from './SystemNotificationBanner/system-notification-banner.js';
import {TimezoneClocks} from './timezone-clocks/timezone-clocks.js';

const tradeAllocationFeature = 'tradeAllocation';
const supportFeature = 'support';

/** Loads Trade Allocation only after an approved non-production user opens it. */
async function loadTradeAllocationModal() {
  const {TradeAllocationModal} = await import('../tradeAllocation/trade-allocation-modal');
  return {default: TradeAllocationModal};
}

const TradeAllocationModal = lazy(loadTradeAllocationModal);

const getAppLabel = (swVersion: string) => {
  const {appVersion} = globalThis as unknown as {appVersion: string};
  switch (getAppEnv()) {
    case 'local': {
      return `local:${appVersion}:${swVersion}`;
    }

    case 'development': {
      return `development:${appVersion}:${swVersion}`;
    }

    case 'staging': {
      return `staging:${appVersion}:${swVersion}`;
    }

    case 'preprod': {
      return `preprod:${appVersion}:${swVersion}`;
    }

    case undefined: {
      return '';
    }

    default: {
      return '';
    }
  }
};

type Props = {
  layout: React.MutableRefObject<ILayoutApi | undefined>;
};

/**
 * Renders QEP shell controls inside the full-width surface used as the
 * installed PWA titlebar when Window Controls Overlay is active.
 */
export function Toolbar({layout}: Props) {
  const [anchorElement, setAnchorElement] = useState<undefined | HTMLElement>(undefined);
  const [preferencesModalOpen, setPreferencesModalOpen] = useState(false);
  const [tradeAllocationModalOpen, setTradeAllocationModalOpen] = useState(false);
  const [appLabel, setAppLabel] = useState('');
  const serviceWorkerVersionRef = useRef('');
  const open = Boolean(anchorElement);
  const tradeAllocationAvailable = getQepSystem().container.canUseFeature(tradeAllocationFeature);
  const supportAvailable = getQepSystem().container.canUseFeature(supportFeature);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(undefined);
  };

  function openPreferencesModal() {
    setPreferencesModalOpen(true);

    handleClose();
  }

  function openIssueReporter() {
    window.open('https://pages.git.cglcloud.com/qep/support/');

    handleClose();
  }

  /** Opens the Trade Allocation chunk only when the preload enables the feature. */
  function openTradeAllocationModal() {
    if (!tradeAllocationAvailable) {
      return;
    }

    setTradeAllocationModalOpen(true);
    handleClose();
  }

  /** Closes and unmounts Trade Allocation so its lazy-loaded UI is released. */
  function closeTradeAllocationModal() {
    setTradeAllocationModalOpen(false);
  }

  /**
   * Completes server logout before disposing API services used by the logout request.
   * The logout promise is contained to prevent a failed request from becoming an unhandled UI rejection,
   * and cleanup still runs after either outcome while the legacy confirmation modal remains skipped.
   */
  function logout() {
    handleClose();
    void (async () => {
      try {
        await getQepSystem().container.authentication.logout();
      } catch (error) {
        console.error('Failed to log out', error);
      } finally {
        cleanupSubscriptionsBeforeLogout();
      }
    })();
  }

  useEffect(() => {
    const syncAppLabel = () => {
      setAppLabel(getAppLabel(serviceWorkerVersionRef.current));
    };

    syncAppLabel();
    requestServiceWorkerVersion(version => {
      serviceWorkerVersionRef.current = version;
      syncAppLabel();
    });

    const intervalId = globalThis.setInterval(() => {
      if (getAppEnv()) {
        syncAppLabel();
        globalThis.clearInterval(intervalId);
      }
    }, 250);

    return () => {
      globalThis.clearInterval(intervalId);
    };
  }, []);

  return (
    <ComponentErrorBoundary>
      <header className='browser-toolbar-surface' aria-label='QEP application header'>
        <ul className='browser-toolbar'>
          <li className='browser-toolbar-segment'>
            <Button
              id='qep-button'
              aria-label='Open QEP menu'
              aria-controls={open ? 'basic-menu' : undefined}
              aria-haspopup='true'
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
            >
              <span>
                <img className='browser-toolbar-logo' src={darkLogo} alt='logo' />
              </span>
              <KeyboardArrowDownIcon className='browser-toolbar-arrow-down' />
            </Button>
            {appLabel
              ? (
                <span className='browser-toolbar-environment-label'>{appLabel}</span>
              )
              : null}
            <Menu
              id='qep-menu'
              anchorEl={anchorElement}
              open={open}
              onClose={handleClose}
              MenuListProps={{'aria-labelledby': 'qep-button'}}
            >
              {tradeAllocationAvailable
                ? <MenuItem onClick={openTradeAllocationModal}>Trade Allocation</MenuItem>
                : null}
              <MenuItem onClick={openPreferencesModal}>Preferences</MenuItem>
              {supportAvailable
                ? <MenuItem onClick={openIssueReporter}>Support</MenuItem>
                : null}
              <MenuItem onClick={logout}>Log out</MenuItem>
            </Menu>

            <PreferencesModal open={preferencesModalOpen} onClose={() => {
              setPreferencesModalOpen(false);
            }} />
            {tradeAllocationAvailable && tradeAllocationModalOpen
              ? (
                <Suspense fallback={null}>
                  <TradeAllocationModal open onClose={closeTradeAllocationModal} />
                </Suspense>
              )
              : null}
          </li>
          <li className={cn('browser-toolbar-segment', 'browser-toolbar-center')}>
            {getQepSystem().container.canUseFeature('systemNotifications')
              ? (
                <div className='browser-toolbar-system-notifications'>
                  <SystemNotificationBanner />
                </div>
              )
              : null}

            <div className='browser-toolbar-clocks-slot'>
              <TimezoneClocks />
            </div>
          </li>
          <li className={cn('browser-toolbar-segment', 'browser-toolbar-right')}>
            <NotificationCenter logout={logout} reportIssue={openIssueReporter} />
            <NetworkStatusMenu />
          </li>
        </ul>
      </header>
    </ComponentErrorBoundary>
  );
}
